import React, { useState, useEffect } from 'react';
import { 
  KeyIcon, 
  BuildingOfficeIcon, 
  UserPlusIcon,
  UsersIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface AdminPanelProps {
  token: string;
  userRole: string;
  userEmail: string;
  superAdminToken?: string | undefined;
}

interface Organization {
  id: string;
  name: string;
  industry: string;
  created_at: string;
  is_active: boolean;
  member_count: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  joined_at: string;
  is_primary: boolean;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ token, userRole, userEmail, superAdminToken }) => {
  // State for different sections
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [orgData, setOrgData] = useState({
    name: '',
    industry: 'Technology',
    adminEmail: '',
    adminName: '',
    adminPassword: ''
  });
  
  const [inviteData, setInviteData] = useState({
    organizationId: '',
    email: '',
    name: '',
    password: '',
    role: 'member'
  });
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '';
  const isSuperAdmin = superAdminToken || ['c.grant@scientistintheloop.com'].includes(userEmail || '');

  // Load organizations if super admin
  useEffect(() => {
    if (isSuperAdmin) {
      loadOrganizations();
    }
  }, [isSuperAdmin]);

  const loadOrganizations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations);
      }
    } catch (err) {
      console.error('Failed to load organizations');
    }
  };

  const loadOrgUsers = async (orgId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/organizations/${orgId}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrgUsers(data.members);
        setSelectedOrg(orgId);
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: resetEmail,
          new_password: newPassword
        })
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        setResetEmail('');
        setNewPassword('');
        setError('');
      } else {
        setError(data.detail);
      }
    } catch (err) {
      setError('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/create-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          super_admin_token: superAdminToken,
          organization_name: orgData.name,
          organization_industry: orgData.industry,
          admin_email: orgData.adminEmail,
          admin_name: orgData.adminName,
          admin_password: orgData.adminPassword
        })
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(`Organization created! Admin can login with email: ${orgData.adminEmail}`);
        setOrgData({
          name: '',
          industry: 'Technology',
          adminEmail: '',
          adminName: '',
          adminPassword: ''
        });
        loadOrganizations(); // Refresh list
        setError('');
      } else {
        setError(data.detail);
      }
    } catch (err) {
      setError('Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const inviteUser = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          organization_id: inviteData.organizationId,
          email: inviteData.email,
          name: inviteData.name,
          password: inviteData.password,
          role: inviteData.role
        })
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        setInviteData({
          organizationId: inviteData.organizationId, // Keep selected org
          email: '',
          name: '',
          password: '',
          role: 'member'
        });
        // Refresh users if viewing this org
        if (selectedOrg === inviteData.organizationId) {
          loadOrgUsers(selectedOrg);
        }
        setError('');
      } else {
        setError(data.detail);
      }
    } catch (err) {
      setError('Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from this organization?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/organizations/${selectedOrg}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        // Refresh users list
        loadOrgUsers(selectedOrg);
        setError('');
      } else {
        setError(data.detail);
      }
    } catch (err) {
      setError('Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const deleteOrganization = async (orgId: string, orgName: string) => {
    if (!window.confirm(`Are you sure you want to delete the organization "${orgName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/organizations/${orgId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        // Refresh organizations list
        loadOrganizations();
        // Clear selected org if it was deleted
        if (selectedOrg === orgId) {
          setSelectedOrg('');
          setOrgUsers([]);
        }
        setError('');
      } else {
        setError(data.detail);
      }
    } catch (err) {
      setError('Failed to delete organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Admin Panel</h2>
      
      {(message || error) && (
        <div className={`mb-4 p-4 rounded ${message ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message || error}
          <button 
            onClick={() => { setMessage(''); setError(''); }}
            className="float-right text-lg font-bold"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Password Reset (Organization Admins) */}
        {userRole === 'admin' && (
          <div className="p-4 border rounded">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <KeyIcon className="h-5 w-5 mr-2" />
              Reset User Password
            </h3>
            <div className="space-y-4">
              <input
                type="email"
                placeholder="User email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full p-2 border rounded"
              />
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-2 border rounded pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-500"
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              <button
                onClick={resetPassword}
                disabled={!resetEmail || !newPassword || loading}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        )}

        {/* Create Organization (Super Admin) */}
        {isSuperAdmin && (
          <div className="p-4 border rounded">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              Create New Organization
            </h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Organization name"
                value={orgData.name}
                onChange={(e) => setOrgData({...orgData, name: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <input
                type="text"
                placeholder="Industry"
                value={orgData.industry}
                onChange={(e) => setOrgData({...orgData, industry: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <input
                type="email"
                placeholder="Admin email"
                value={orgData.adminEmail}
                onChange={(e) => setOrgData({...orgData, adminEmail: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <input
                type="text"
                placeholder="Admin name (optional)"
                value={orgData.adminName}
                onChange={(e) => setOrgData({...orgData, adminName: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <input
                type="password"
                placeholder="Admin password"
                value={orgData.adminPassword}
                onChange={(e) => setOrgData({...orgData, adminPassword: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <button
                onClick={createOrganization}
                disabled={!orgData.name || !orgData.adminEmail || !orgData.adminPassword || loading}
                className="px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-300"
              >
                {loading ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </div>
        )}

        {/* Invite User (Super Admin) */}
        {isSuperAdmin && (
          <div className="p-4 border rounded">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <UserPlusIcon className="h-5 w-5 mr-2" />
              Invite User to Organization
            </h3>
            <div className="space-y-4">
              <select
                value={inviteData.organizationId}
                onChange={(e) => setInviteData({...inviteData, organizationId: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="">Select organization</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <input
                type="email"
                placeholder="User email"
                value={inviteData.email}
                onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <input
                type="text"
                placeholder="User name"
                value={inviteData.name}
                onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <input
                type="password"
                placeholder="User password"
                value={inviteData.password}
                onChange={(e) => setInviteData({...inviteData, password: e.target.value})}
                className="w-full p-2 border rounded"
              />
              <select
                value={inviteData.role}
                onChange={(e) => setInviteData({...inviteData, role: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
              <button
                onClick={inviteUser}
                disabled={!inviteData.organizationId || !inviteData.email || !inviteData.name || !inviteData.password || loading}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
              >
                {loading ? 'Inviting...' : 'Invite User'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Organizations List (Super Admin) */}
      {isSuperAdmin && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <UsersIcon className="h-5 w-5 mr-2" />
            All Organizations
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Industry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {organizations.map(org => (
                  <tr key={org.id} className={selectedOrg === org.id ? 'bg-blue-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500">{org.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {org.industry}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {org.member_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(org.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => loadOrgUsers(org.id)}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          View Users <ChevronRightIcon className="h-4 w-4 ml-1" />
                        </button>
                        <button
                          onClick={() => deleteOrganization(org.id, org.name)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                          title="Delete Organization"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Organization Users */}
          {selectedOrg && orgUsers.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded">
              <h4 className="font-semibold mb-3">
                Users in {organizations.find(o => o.id === selectedOrg)?.name}
              </h4>
              <div className="space-y-2">
                {orgUsers.map(user => (
                  <div key={user.id} className="flex justify-between items-center p-2 bg-white rounded">
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                          user.role === 'viewer' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role}
                        </span>
                        {user.is_primary && (
                          <span className="ml-2 text-xs text-green-600">Primary</span>
                        )}
                      </div>
                      <button
                        onClick={() => removeUser(user.id, user.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Remove user from organization"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 p-4 bg-gray-100 rounded">
        <h4 className="font-semibold mb-2">Admin Features:</h4>
        <ul className="text-sm space-y-1">
          <li>• <strong>Organization Admins</strong>: Can reset passwords for users in their organization</li>
          {isSuperAdmin && (
            <>
              <li>• <strong>Super Admins</strong>: Can create organizations and invite users to any organization</li>
              <li>• <strong>View All Organizations</strong>: See all organizations and their member counts</li>
              <li>• <strong>View Users</strong>: Click on any organization to see its users and roles</li>
              <li>• <strong>Remove Users</strong>: Click the × button next to any user to remove them from the organization</li>
              <li>• <strong>Delete Organizations</strong>: Click the trash icon to delete empty organizations</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default AdminPanel;